import { html, View } from 'rune-ts';
import klass from './BadgeSubView.module.scss';
import { classes } from '@services/MarppleShop/shared/util';
import { font } from '@services/MarppleShop/shared/typography';
import { differenceInMinutes } from 'date-fns';
import { TimerSubscriptionManager } from '@services/MarppleShop/renderApp/components/cells/ProductBadgeList/utils/TimerSubscriptionManager.ts';
import { TimerTick } from '@services/MarppleShop/renderApp/components/cells/ProductBadgeList/utils/timer.ts';
import { CardProductError } from '@services/MarppleShop/renderApp/pages/Curation/components/CardProductView.ts';

export type TProps =
  | {
      type: 'Free_Shipping';
      size: 'Large (Xlarge~Large)';
    }
  | {
      type: 'Time_left';
      size: 'Large (Xlarge~Large)';
      sell_end_at: Date;
    }
  | {
      type: 'Stock_left';
      size: 'Large (Xlarge~Large)';
      quantity: number;
      order_count: number;
    }
  | {
      type: 'Popular';
      size: 'Large (Xlarge~Large)';
    }
  | {
      type: 'Limited';
      size: 'Large (Xlarge~Large)';
      quantity: number;
    }
  | {
      type: 'Trend';
      size: 'Large (Xlarge~Large)';
    }
  | {
      type: 'Free_Shipping';
      size: 'Medium (Medium~Small)';
    }
  | {
      type: 'Time_left';
      size: 'Medium (Medium~Small)';
      sell_end_at: Date;
    }
  | {
      type: 'Stock_left';
      size: 'Medium (Medium~Small)';
      quantity: number;
      order_count: number;
    }
  | {
      type: 'Popular';
      size: 'Medium (Medium~Small)';
    }
  | {
      type: 'Limited';
      size: 'Medium (Medium~Small)';
      quantity: number;
    }
  | {
      type: 'Trend';
      size: 'Medium (Medium~Small)';
    };

export type TBadgeSubWithoutSize =
  | {
      type: 'Free_Shipping';
    }
  | {
      type: 'Time_left';
      sell_end_at: Date;
    }
  | {
      type: 'Stock_left';
      quantity: number;
      order_count: number;
    }
  | {
      type: 'Popular';
    }
  | {
      type: 'Limited';
      quantity: number;
    }
  | {
      type: 'Trend';
    }
  | {
      type: 'Free_Shipping';
    }
  | {
      type: 'Time_left';
      sell_end_at: Date;
    }
  | {
      type: 'Stock_left';
      quantity: number;
      order_count: number;
    }
  | {
      type: 'Popular';
    }
  | {
      type: 'Limited';
      quantity: number;
    }
  | {
      type: 'Trend';
    };

/**
 * https://www.figma.com/design/J3pxvmlv167DXuLToyL3Mg/%EC%83%81%ED%92%88-%EC%B9%B4%EB%93%9C-UI?node-id=401-41160&m=dev
 */
export class BadgeSubView extends View<TProps> {
  private timerSubscription: TimerSubscriptionManager = new TimerSubscriptionManager(this);

  private now_timestamp: number = Date.now();

  onTimerTick(event: TimerTick): void {
    this.now_timestamp = event.detail.timestamp;
    this.redraw();
  }

  destroy(): void {
    this.timerSubscription.unsubscribe();
  }

  protected override onRender(): void {
    this.timerSubscription.subscribe();
  }

  protected override onUnmount(): void {
    this.destroy();
  }

  protected override template(props: TProps) {
    if (props.type === 'Free_Shipping' && props.size === 'Large (Xlarge~Large)') {
      return html`<span class="${classes(klass.type_free_shipping, klass.size_large, font('12_semibold'))}">
        ${ET('card_product::badge_sub::free_shipping')}
      </span>`;
    } else if (props.type === 'Time_left' && props.size === 'Large (Xlarge~Large)') {
      const { sell_end_at } = props;

      const minutes = differenceInMinutes(new Date(sell_end_at), new Date(this.now_timestamp));

      return html`<span class="${classes(klass.type_time_left, klass.size_large, font('12_semibold'))}">
        <span>${ET('card_product::badge_sub::time_left_until_end')}</span>
        <span>${ET('card_product::badge_sub::time_left_minutes', { minutes })}</span>
      </span>`;
    } else if (props.type === 'Stock_left' && props.size === 'Large (Xlarge~Large)') {
      const { quantity, order_count } = props;

      return html`<span class="${classes(klass.type_stock_left, klass.size_large, font('12_semibold'))}">
        ${ET('card_product::badge_sub::stock_left', { count: quantity - order_count })}
      </span>`;
    } else if (props.type === 'Popular' && props.size === 'Large (Xlarge~Large)') {
      return html`<span class="${classes(klass.type_popular, klass.size_large, font('12_semibold'))}">
        ${ET('card_product::badge_sub::popular')}
      </span>`;
    } else if (props.type === 'Limited' && props.size === 'Large (Xlarge~Large)') {
      const { quantity } = props;

      return html`<span class="${classes(klass.type_limited, klass.size_large, font('12_semibold'))}">
        ${ET('card_product::badge_sub::limited', { quantity })}
      </span>`;
    } else if (props.type === 'Trend' && props.size === 'Large (Xlarge~Large)') {
      return html`<span class="${classes(klass.type_trend, klass.size_large, font('12_semibold'))}">
        ${ET('card_product::badge_sub::trend')}
      </span>`;
    } else if (props.type === 'Free_Shipping' && props.size === 'Medium (Medium~Small)') {
      return html`<span class="${classes(klass.type_free_shipping, klass.size_medium, font('10_semibold'))}">
        <span>${ET('card_product::badge_sub::free_shipping')}</span>
      </span>`;
    } else if (props.type === 'Time_left' && props.size === 'Medium (Medium~Small)') {
      const { sell_end_at } = props;

      const minutes = differenceInMinutes(new Date(sell_end_at), new Date(this.now_timestamp));

      return html`<span class="${classes(klass.type_time_left, klass.size_medium, font('10_semibold'))}">
        <span>${ET('card_product::badge_sub::time_left_until_end')}</span>
        <span>${ET('card_product::badge_sub::time_left_minutes', { minutes })}</span>
      </span>`;
    } else if (props.type === 'Stock_left' && props.size === 'Medium (Medium~Small)') {
      const { quantity, order_count } = props;

      return html`<span class="${classes(klass.type_stock_left, klass.size_medium, font('10_semibold'))}">
        ${ET('card_product::badge_sub::stock_left', { count: quantity - order_count })}
      </span>`;
    } else if (props.type === 'Popular' && props.size === 'Medium (Medium~Small)') {
      return html`<span class="${classes(klass.type_popular, klass.size_medium, font('10_semibold'))}"
                        ${ET('card_product::badge_sub::popular')}</span
      >`;
    } else if (props.type === 'Limited' && props.size === 'Medium (Medium~Small)') {
      const { quantity } = props;

      return html`<span class="${classes(klass.type_limited, klass.size_medium, font('10_semibold'))}"
        >${ET('card_product::badge_sub::limited', { quantity })}</span
      >`;
    } else if (props.type === 'Trend' && props.size === 'Medium (Medium~Small)') {
      return html`<span class="${classes(klass.type_trend, klass.size_medium, font('10_semibold'))}"
        >${ET('card_product::badge_sub::trend')}</span
      >`;
    } else {
      throw new CardProductError();
    }
  }
}
